/**
 * Standort-Check-in (27.10.2026, Wettbewerbsbeobachtung Runde 2).
 *
 * Eine Web-App bekommt auf dem iPhone **im Hintergrund keinen Standort** — die
 * automatische Ankunftserkennung kommt erst mit der nativen App. Gebaut ist,
 * was im Vordergrund geht: ein Tipp auf „Bin angekommen“ oder „Ich bin hier“,
 * und „Ort teilen, solange die App offen ist“, das nur aktualisiert, solange
 * die App vorn ist. IP-Ortung gibt es bewusst nicht: im Mobilfunk oft über
 * 100 km daneben, und sie zeigte den Ort dem Server.
 *
 * Koordinaten, Ortsnamen und Genauigkeit sind Ende-zu-Ende verschlüsselt
 * (`SEALED_EMPTY.places`/`location_checkins`, immer — auch bevor der Haushalt
 * sonst verschlüsselt, `ALWAYS_SEALED_TABLES`). App, Scheduler und Service
 * Worker lesen dieselben Regeln hier.
 */

/** `arrived`: „Bin angekommen“, `here`: „Ich bin hier“, `live`: teilen, solange die App offen ist. */
export const CHECKIN_KINDS = ['arrived', 'here', 'live'] as const;
export type CheckinKind = (typeof CHECKIN_KINDS)[number];

export const PLACE_NAME_MAX = 60;
/** Kleiner als 25 m ist kein Ort, den ein Telefon im Haus noch trifft. */
export const PLACE_RADIUS_MIN_M = 25;
export const PLACE_RADIUS_MAX_M = 5000;
export const PLACE_RADIUS_DEFAULT_M = 150;
/** Was ein Gerät als Genauigkeit meldet, ab hier ist es eher eine Gegend. */
export const ACCURACY_COARSE_M = 500;
export const ACCURACY_MAX_M = 100_000;

/**
 * Aufbewahrung (Entscheidung 27.10.2026): Verlauf sieben Tage, danach bleibt
 * je Person nur der **letzte** Check-in stehen. Wo jemand vor drei Wochen war,
 * braucht kein Haushalt — und was nicht gespeichert ist, kann nicht
 * herauskommen. Gelöscht wird weich und ohne Chiffrat (`pruneCheckins` im
 * Scheduler), damit jedes Gerät es erfährt; die Oberfläche sagt die Regel.
 */
export const CHECKIN_HISTORY_DAYS = 7;

/** „Ort teilen, solange die App offen ist“: so oft neu orten, nur im Vordergrund. */
export const LIVE_UPDATE_MS = 3 * 60 * 1000;
/** Wählbare Dauer in Minuten. */
export const LIVE_DURATIONS_MIN = [15, 30, 60, 120] as const;

/** Zeitlimit für eine Ortung (`navigator.geolocation`, hohe Genauigkeit). */
export const GEO_TIMEOUT_MS = 20_000;

/**
 * Eine Mitteilung an den Haushalt nur, solange sie noch stimmt: Lag ein
 * Check-in offline in der Outbox, ist „Mia ist angekommen“ nach einer Stunde
 * keine Nachricht mehr. Er steht dann in der Liste, gemeldet wird er nicht.
 */
export const CHECKIN_NOTICE_MAX_AGE_MS = 30 * 60 * 1000;

export interface GeoPoint {
  latitude: number;
  longitude: number;
}

export function isGeoPoint(value: unknown): value is GeoPoint {
  if (!value || typeof value !== 'object') return false;
  const { latitude, longitude } = value as Record<string, unknown>;
  return (
    typeof latitude === 'number' &&
    typeof longitude === 'number' &&
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    Math.abs(latitude) <= 90 &&
    Math.abs(longitude) <= 180
  );
}

const EARTH_RADIUS_M = 6_371_008.8;
const rad = (degrees: number) => (degrees * Math.PI) / 180;

/** Entfernung zweier Punkte in Metern (Haversine — für Orte im Alltag genau genug). */
export function distanceM(a: GeoPoint, b: GeoPoint): number {
  const dLat = rad(b.latitude - a.latitude);
  const dLon = rad(b.longitude - a.longitude);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(a.latitude)) * Math.cos(rad(b.latitude)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

export interface PlaceLike {
  id: string;
  latitude?: number | null;
  longitude?: number | null;
  name: string;
  radiusM?: number | null;
  deletedAt?: string | null;
}

/** Nur Orte mit Mittelpunkt und Radius zählen — ein verschlossener ohne Inhalt nicht. */
function usable<T extends PlaceLike>(
  place: T,
): place is T & { latitude: number; longitude: number; radiusM: number } {
  return (
    !place.deletedAt &&
    isGeoPoint(place) &&
    typeof place.radiusM === 'number' &&
    place.radiusM > 0 &&
    place.name.trim().length > 0
  );
}

/** Der nächste bekannte Ort — mit Entfernung, ob drin oder nicht. `null`: keiner. */
export function nearestPlace<T extends PlaceLike>(
  point: GeoPoint,
  places: readonly T[],
): { place: T; distanceM: number; inside: boolean } | null {
  let best: { place: T; distanceM: number; inside: boolean } | null = null;
  for (const place of places) {
    if (!usable(place)) continue;
    const distance = distanceM(point, place);
    if (!best || distance < best.distanceM) {
      best = { place, distanceM: distance, inside: distance <= place.radiusM };
    }
  }
  return best;
}

/**
 * Welcher Ort wird vorgeschlagen? Der, in dessen Radius der Punkt liegt —
 * bei überlappenden der nächste. Die Genauigkeit vergrößert den Radius
 * bewusst nicht: Bei 800 m Ungenauigkeit „Schule“ vorzuschlagen, wäre geraten.
 */
export function placeAt<T extends PlaceLike>(point: GeoPoint, places: readonly T[]): T | null {
  let best: { place: T; distance: number } | null = null;
  for (const place of places) {
    if (!usable(place)) continue;
    const distance = distanceM(point, place);
    if (distance > place.radiusM) continue;
    if (!best || distance < best.distance) best = { place, distance };
  }
  return best?.place ?? null;
}

interface CheckinLike {
  id: string;
  userId?: string | undefined;
  updatedAt: string;
  deletedAt?: string | null;
  recordedAt?: string | null;
}

/** Wann der Ort bestimmt wurde — sonst die letzte Änderung. */
export function checkinTime(checkin: CheckinLike): string {
  return checkin.recordedAt ?? checkin.updatedAt;
}

/** Der letzte Check-in je Person, neueste zuerst. */
export function latestPerPerson<T extends CheckinLike>(checkins: readonly T[]): T[] {
  const latest = new Map<string, T>();
  for (const checkin of checkins) {
    if (checkin.deletedAt || !checkin.userId) continue;
    const current = latest.get(checkin.userId);
    if (!current || checkinTime(checkin) > checkinTime(current)) {
      latest.set(checkin.userId, checkin);
    }
  }
  return [...latest.values()].sort((a, b) => checkinTime(b).localeCompare(checkinTime(a)));
}

/**
 * Was die Aufbewahrung behält: alles der letzten sieben Tage und je Person
 * den letzten — dieselbe Regel wie `pruneCheckins` beim Server.
 */
export function keptCheckins<T extends CheckinLike>(checkins: readonly T[], now: Date): T[] {
  const cutoff = now.getTime() - CHECKIN_HISTORY_DAYS * 24 * 60 * 60 * 1000;
  const latest = new Set(latestPerPerson(checkins).map((checkin) => checkin.id));
  return checkins
    .filter(
      (checkin) =>
        !checkin.deletedAt &&
        (latest.has(checkin.id) || Date.parse(checkinTime(checkin)) >= cutoff),
    )
    .sort((a, b) => checkinTime(b).localeCompare(checkinTime(a)));
}

/** Läuft das Teilen noch? Nur `live` mit Ende in der Zukunft. */
export function isLiveActive(
  checkin: { kind?: string | null; liveUntil?: string | null; deletedAt?: string | null },
  now: Date,
): boolean {
  return (
    checkin.kind === 'live' &&
    !checkin.deletedAt &&
    typeof checkin.liveUntil === 'string' &&
    Date.parse(checkin.liveUntil) > now.getTime()
  );
}

/**
 * Link „in Karten öffnen“ — lokal erzeugt, geöffnet erst auf Tippen. Apple
 * Karten öffnet auf dem iPhone die App und sonst die Webkarte.
 */
export function mapsUrl(point: GeoPoint, label?: string | null): string {
  const ll = `${point.latitude.toFixed(6)},${point.longitude.toFixed(6)}`;
  const query = label?.trim() ? `&q=${encodeURIComponent(label.trim())}` : '';
  return `https://maps.apple.com/?ll=${ll}${query}`;
}

// --- Mitteilung --------------------------------------------------------------

/**
 * Inhalt der Mitteilung an den Haushalt — das Gerät verschlüsselt ihn mit dem
 * Haushaltsschlüssel (`notice`), der Service Worker der anderen öffnet ihn.
 * Der Server leitet nur weiter.
 */
export interface CheckinNotice {
  kind: CheckinKind;
  /** Wer — so, wie der Haushalt die Person nennt. */
  name: string;
  /** Ortsname, wenn einer gewählt wurde. */
  place: string | null;
  /** Bei `live`: bis wann geteilt wird (ISO). */
  until?: string | null;
}

export function isCheckinNotice(value: unknown): value is CheckinNotice {
  if (!value || typeof value !== 'object') return false;
  const notice = value as Record<string, unknown>;
  return (
    (CHECKIN_KINDS as readonly unknown[]).includes(notice.kind) &&
    typeof notice.name === 'string' &&
    (notice.place === null || typeof notice.place === 'string')
  );
}

export type NoticeLocale = 'de' | 'en';

export function noticeLocale(value: unknown): NoticeLocale {
  return typeof value === 'string' && value.toLowerCase().startsWith('en') ? 'en' : 'de';
}

export interface NoticeText {
  title: string;
  body: string;
}

/** Was ankommt, wenn der Service Worker nicht öffnen kann — nie still (iOS-Regel). */
export function checkinFallbackText(locale: NoticeLocale): NoticeText {
  return locale === 'en'
    ? { title: 'Kraftwerk', body: 'New location in your household' }
    : { title: 'Kraftwerk', body: 'Neuer Standort im Haushalt' };
}

function clock(iso: string | null | undefined, timeZone?: string): string | null {
  if (!iso || Number.isNaN(Date.parse(iso))) return null;
  return new Intl.DateTimeFormat('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  }).format(new Date(iso));
}

/** Der geöffnete Text — in der Sprache des Empfängers. */
export function checkinNoticeText(
  notice: CheckinNotice,
  locale: NoticeLocale,
  timeZone?: string,
): NoticeText {
  const name = notice.name.trim().slice(0, 60) || (locale === 'en' ? 'Someone' : 'Jemand');
  const place = notice.place?.trim().slice(0, PLACE_NAME_MAX) || null;
  const until = clock(notice.until, timeZone);
  if (locale === 'en') {
    switch (notice.kind) {
      case 'arrived':
        return { title: 'Arrived', body: place ? `${name} arrived: ${place}` : `${name} arrived` };
      case 'here':
        return {
          title: 'Location shared',
          body: place ? `${name} is here: ${place}` : `${name} shared where they are`,
        };
      case 'live':
        return {
          title: 'Sharing location',
          body: `${name} is sharing their location while the app is open${until ? ` (until ${until})` : ''}`,
        };
    }
  }
  switch (notice.kind) {
    case 'arrived':
      return {
        title: 'Angekommen',
        body: place ? `${name} ist angekommen: ${place}` : `${name} ist angekommen`,
      };
    case 'here':
      return {
        title: 'Standort geteilt',
        body: place ? `${name} ist hier: ${place}` : `${name} hat geteilt, wo sie/er ist`,
      };
    case 'live':
      return {
        title: 'Ort wird geteilt',
        body: `${name} teilt den Ort, solange die App offen ist${until ? ` (bis ${until})` : ''}`,
      };
  }
}
